/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

/* eslint-disable new-cap */

import {lazy} from 'react';

import AppMenu from './components/AppMenu';
import FullscreenSwitcher from './components/FullscreenSwitcher';
import SearchBox from './components/SearchBox';
import Toolbar from './components/Toolbar';
import APIPlugin from './plugins/API';
import AttributeTablePlugin from './plugins/AttributeTable';
import AuthenticationPlugin from './plugins/Authentication';
import BackgroundSwitcherPlugin from './plugins/BackgroundSwitcher';
import BookmarkPlugin from './plugins/Bookmark';
import BottomBarPlugin from './plugins/BottomBar';
import CookiePopupPlugin from './plugins/CookiePopup';
import EditingPlugin from './plugins/Editing';
import FeatureFormPlugin from './plugins/FeatureForm';
import FeatureSearchPlugin from './plugins/FeatureSearch';
import FeatureSelectionPlugin from './plugins/FeatureSelection';
import HelpPlugin from './plugins/Help';
import HomeButtonPlugin from './plugins/HomeButton';
import IdentifyPlugin from './plugins/Identify';
import LayerCatalogPlugin from './plugins/LayerCatalog';
import LayerTreePlugin from './plugins/LayerTree';
import LocateButtonPlugin from './plugins/LocateButton';
import MapPlugin from './plugins/Map';
import EditingSupport from './plugins/map/EditingSupport';
import LocateSupport from './plugins/map/LocateSupport';
import MeasurementSupport from './plugins/map/MeasurementSupport';
import RedliningSupport from './plugins/map/RedliningSupport';
import SnappingSupport from './plugins/map/SnappingSupport';
import MapComparePlugin from './plugins/MapCompare';
import MapCopyrightPlugin from './plugins/MapCopyright';
import MapDropImportPlugin from './plugins/MapDropImport';
import MapExportPlugin from './plugins/MapExport';
import MapFilterPlugin from './plugins/MapFilter';
import MapInfoTooltipPlugin from './plugins/MapInfoTooltip';
import MapLegendPlugin from './plugins/MapLegend';
import MapTipPlugin from './plugins/MapTip';
import MeasurePlugin from './plugins/Measure';
import NewsPopupPlugin from './plugins/NewsPopup';
import ObjectListPlugin from './plugins/ObjectList';
import OverviewMapPlugin from './plugins/OverviewMap';
import PortalPlugin from './plugins/Portal';
import PrintPlugin from './plugins/Print';
import RedliningPlugin from './plugins/Redlining';
import RedliningBufferSupport from './plugins/redlining/RedliningBufferSupport';
import RedliningFeatureLabelSupport from './plugins/redlining/RedliningFeatureLabelSupport';
import ReportsPlugin from './plugins/Reports';
import RoutingPlugin from './plugins/Routing';
import ScratchDrawingPlugin from './plugins/ScratchDrawing';
import ServiceInfoPlugin from './plugins/ServiceInfo';
import SettingsPlugin from './plugins/Settings';
import SharePlugin from './plugins/Share';
import StartupMarkerPlugin from './plugins/StartupMarker';
import TaskButtonPlugin from './plugins/TaskButton';
import ThemeBrowserPlugin from './plugins/ThemeBrowser';
import ThemeSwitcherPlugin from './plugins/ThemeSwitcher';
import TimeManagerPlugin from './plugins/TimeManager';
import TopBarPlugin from './plugins/TopBar';
import View3DPlugin from './plugins/View3D';
import VisibilityPresetsPlugin from './plugins/VisibilityPreset';
import {ZoomInPlugin, ZoomOutPlugin} from './plugins/ZoomButtons';
import defaultLocaleData from './static/translations/en-US.json';

export default {
    defaultLocaleData: defaultLocaleData,
    initialState: {
        defaultState: {},
        mobile: {}
    },
    pluginsDef: {
        plugins: {
            MapPlugin: MapPlugin({
                EditingSupport: EditingSupport,
                MeasurementSupport: MeasurementSupport,
                LocateSupport: LocateSupport,
                RedliningSupport: RedliningSupport,
                SnappingSupport: SnappingSupport
            }),
            APIPlugin: APIPlugin,
            AttributeTablePlugin: AttributeTablePlugin(/* CustomEditingInterface */),
            AuthenticationPlugin: AuthenticationPlugin,
            BackgroundSwitcherPlugin: BackgroundSwitcherPlugin,
            BookmarkPlugin: BookmarkPlugin,
            VisibilityPresetsPlugin: VisibilityPresetsPlugin,
            BottomBarPlugin: BottomBarPlugin,
            CookiePopupPlugin: CookiePopupPlugin,
            CyclomediaPlugin: lazy(() => import('./plugins/Cyclomedia')),
            EditingPlugin: EditingPlugin(/* CustomEditingInterface */),
            FeatureFormPlugin: FeatureFormPlugin(/* CustomEditingInterface */),
            FeatureSelectionPlugin: FeatureSelectionPlugin,
            GeometryDigitizerPlugin: lazy(() => import('./plugins/GeometryDigitizer')),
            HeightProfilePlugin: lazy(() => import('./plugins/HeightProfile')),
            HelpPlugin: HelpPlugin(),
            HomeButtonPlugin: HomeButtonPlugin,
            IdentifyPlugin: IdentifyPlugin,
            LayerCatalogPlugin: LayerCatalogPlugin,
            LayerTreePlugin: LayerTreePlugin,
            LocateButtonPlugin: LocateButtonPlugin,
            MapComparePlugin: MapComparePlugin,
            MapCopyrightPlugin: MapCopyrightPlugin,
            MapDropImportPlugin: MapDropImportPlugin,
            MapExportPlugin: MapExportPlugin,
            MapFilterPlugin: MapFilterPlugin,
            MapInfoTooltipPlugin: MapInfoTooltipPlugin(),
            MapLegendPlugin: MapLegendPlugin,
            MapTipPlugin: MapTipPlugin,
            MeasurePlugin: MeasurePlugin,
            NewsPopupPlugin: NewsPopupPlugin,
            ObjectListPlugin: ObjectListPlugin(/* CustomEditingInterface */),
            ObliqueViewPlugin: lazy(() => import('./plugins/ObliqueView')),
            OverviewMapPlugin: OverviewMapPlugin,
            PanoramaxPlugin: lazy(() => import('./plugins/Panoramax')),
            PortalPlugin: PortalPlugin,
            PrintPlugin: PrintPlugin,
            RedliningPlugin: RedliningPlugin({
                BufferSupport: RedliningBufferSupport,
                FeatureLabelSupport: RedliningFeatureLabelSupport
            }),
            ReportsPlugin: ReportsPlugin,
            RoutingPlugin: RoutingPlugin,
            FeatureSearchPlugin: FeatureSearchPlugin,
            ScratchDrawingPlugin: ScratchDrawingPlugin,
            SensorThingsToolPlugin: lazy(() => import('./plugins/SensorThingsTool')),
            ServiceInfoPlugin: ServiceInfoPlugin,
            SettingsPlugin: SettingsPlugin,
            SharePlugin: SharePlugin,
            StartupMarkerPlugin: StartupMarkerPlugin,
            TaskButtonPlugin: TaskButtonPlugin,
            ThemeBrowserPlugin: ThemeBrowserPlugin,
            ThemeSwitcherPlugin: ThemeSwitcherPlugin,
            TimeManagerPlugin: TimeManagerPlugin,
            TopBarPlugin: TopBarPlugin({
                AppMenu: AppMenu,
                Search: SearchBox,
                Toolbar: Toolbar,
                FullscreenSwitcher: FullscreenSwitcher
            }),
            TourGuidePlugin: lazy(() => import('./plugins/TourGuide')),
            ValueToolPlugin: lazy(() => import('./plugins/ValueTool')),
            View3DPlugin: View3DPlugin({
                BackgroundSwitcher3D: lazy(() => import('./plugins/map3d/BackgroundSwitcher3D')),
                BottomBar3D: lazy(() => import('./plugins/map3d/BottomBar3D')),
                Compare3D: lazy(() => import('./plugins/map3d/Compare3D')),
                Draw3D: lazy(() => import('./plugins/map3d/Draw3D')),
                ExportObjects3D: lazy(() => import('./plugins/map3d/ExportObjects3D')),
                HideObjects3D: lazy(() => import('./plugins/map3d/HideObjects3D')),
                Identify3D: lazy(() => import('./plugins/map3d/Identify3D')),
                LayerTree3D: lazy(() => import('./plugins/map3d/LayerTree3D')),
                MapCopyright3D: lazy(() => import('./plugins/map3d/MapCopyright3D')),
                MapExport3D: lazy(() => import('./plugins/map3d/MapExport3D')),
                MapLight3D: lazy(() => import('./plugins/map3d/MapLight3D')),
                Measure3D: lazy(() => import('./plugins/map3d/Measure3D')),
                MeasureObjects3D: lazy(() => import('./plugins/map3d/MeasureObjects3D')),
                OverviewMap3D: lazy(() => import('./plugins/map3d/OverviewMap3D')),
                Settings3D: lazy(() => import('./plugins/map3d/Settings3D')),
                SunExposure3D: lazy(() => import('./plugins/map3d/SunExposure3D')),
                TopBar3D: lazy(() => import('./plugins/map3d/TopBar3D')),
                Viewshed3D: lazy(() => import('./plugins/map3d/Viewshed3D'))
            }),
            ZoomInPlugin: ZoomInPlugin,
            ZoomOutPlugin: ZoomOutPlugin
        },
        cfg: {
        }
    },
    actionLogger: (action) => {
        /* Do something with action, i.e. Piwik/Mamoto event tracking */
    }
    /*
    themeLayerRestorer: (missingLayers, theme, callback) => {
        // Invoked for layers specified in the l url parameter which are missing in the specified theme
        // Could be used to query a search provider for the missing theme layers

        // A list of theme layers to merge into the theme
        const newLayers = [];

        // A dictionary mapping the name of the searched layer name with the resulting layer name(s) as an array, i.e.
        // {searchlayername: ["resultlayername1", "resultlayername2"], ...}
        const newLayerNames = {};

        callback(newLayers, newLayerNames);
    }*/
    /* externalLayerRestorer: (externalLayers, themes, callback) => {
        // Optional function to handle restoring of external layers from the l URL parameter
        // If omitted, the default handler is used which downloads capabilities for each service to restore the layer
    }*/
};
